import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { ARTICLE_CATEGORIES } from '../../utils/constants';
import { categoryLabel } from '../../utils/labels';
import { ContentBlocks } from '../article/ContentBlocks';
import { ShellCard } from '../layout/ShellCard';
import { RichTextEditor } from './RichTextEditor';

export function ArticleEditor({
  initialTitle = '',
  initialSummary = '',
  initialHtml = '',
  initialCategories = ['news'],
  busy = false,
  readOnly = false,
  onSaveDraft,
  onSubmit,
  onDirtyChange,
}) {
  const [title, setTitle] = useState(initialTitle);
  const [summary, setSummary] = useState(initialSummary);
  const [html, setHtml] = useState(initialHtml);
  const [category, setCategory] = useState(
    (initialCategories || []).find((c) => ARTICLE_CATEGORIES.includes(c)) ||
      'news',
  );
  const [tab, setTab] = useState('edit');

  useEffect(() => {
    const initialCat =
      (initialCategories || []).find((c) => ARTICLE_CATEGORIES.includes(c)) ||
      'news';
    onDirtyChange?.(
      title !== initialTitle ||
        summary !== initialSummary ||
        html !== initialHtml ||
        category !== initialCat,
    );
  }, [
    title,
    summary,
    html,
    category,
    initialTitle,
    initialSummary,
    initialHtml,
    initialCategories,
    onDirtyChange,
  ]);

  function buildPayload() {
    return {
      title: title.trim(),
      summary: summary.trim(),
      blocks: [{ id: 'b1', type: 'text', html }],
      categories: [category],
    };
  }

  return (
    <ShellCard>
      <Stack spacing={2.5}>
        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          fullWidth
          disabled={readOnly || busy}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Summary"
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          required
          fullWidth
          multiline
          rows={2}
          disabled={readOnly || busy}
          InputLabelProps={{ shrink: true }}
        />

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Category
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {ARTICLE_CATEGORIES.map((c) => (
              <Chip
                key={c}
                clickable
                label={categoryLabel(c)}
                color={category === c ? 'secondary' : 'default'}
                variant={category === c ? 'filled' : 'outlined'}
                onClick={() => !readOnly && !busy && setCategory(c)}
                disabled={readOnly || busy}
              />
            ))}
          </Stack>
        </Box>

        <Box>
          <Tabs
            value={tab}
            onChange={(_e, v) => setTab(v)}
            sx={{
              mb: 1.5,
              minHeight: 40,
              '& .MuiTab-root': {
                color: 'text.secondary',
                minHeight: 40,
                textTransform: 'none',
                fontWeight: 600,
              },
              '& .Mui-selected': {
                color: 'secondary.main',
              },
            }}
          >
            <Tab label="Edit" value="edit" />
            <Tab label="Preview" value="preview" />
          </Tabs>

          {tab === 'edit' ? (
            <RichTextEditor
              value={html}
              onChange={setHtml}
              disabled={readOnly || busy}
              placeholder="Write the article body…"
            />
          ) : (
            <Box
              className="content-html"
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                color: 'text.primary',
              }}
            >
              {html ? (
                <ContentBlocks
                  blocks={[{ id: 'preview', type: 'text', html }]}
                />
              ) : (
                <Typography color="text.secondary">
                  Nothing to preview yet.
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {!readOnly && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              disabled={busy || !title.trim() || !summary.trim()}
              onClick={() => onSaveDraft?.(buildPayload())}
            >
              Save draft
            </Button>
            {onSubmit && (
              <Button
                variant="outlined"
                color="secondary"
                disabled={busy || !title.trim() || !summary.trim()}
                onClick={() => onSubmit?.(buildPayload())}
              >
                Submit for review
              </Button>
            )}
          </Stack>
        )}
      </Stack>
    </ShellCard>
  );
}
